import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./RepairLayout.css";
import Sidebar, { SidebarMenuItem } from "../components/Sidebar";
import DiagnosisView from "./diagnosis/DiagnosisView";
import RecommendationView from "./recommendation/RecommendationView";
import RequestView from "./request/RequestView";
import StatusView from "./status/StatusView";
import { FaultCode, RepairShop } from "./repairData";

export interface DiagnosisResult {
    fault: FaultCode;
    vehicleNumber: string;
}

const repairMenuItems: SidebarMenuItem[] = [
    {
        id: "diagnosis",
        label: "고장진단",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
        ),
    },
    {
        id: "recommendation",
        label: "업체추천",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
            </svg>
        ),
    },
    {
        id: "request",
        label: "수리요청",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
        ),
    },
    {
        id: "status",
        label: "정비현황",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
            </svg>
        ),
    },
];

export default function RepairLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("diagnosis");

    // Shared state across sub-menus
    const [diagnosisResult, setDiagnosisResult] = useState<DiagnosisResult | null>(null);
    const [selectedShop, setSelectedShop] = useState<RepairShop | null>(null);

    useEffect(() => {
        const path = location.pathname;
        if (path.includes("/repair/recommendation")) setActiveTab("recommendation");
        else if (path.includes("/repair/request")) setActiveTab("request");
        else if (path.includes("/repair/status")) setActiveTab("status");
        else setActiveTab("diagnosis");
    }, [location]);

    const handleMenuSelect = (id: string) => {
        setActiveTab(id);
        navigate(`/repair/${id}`);
    };

    return (
        <div className="repair-layout">
            <Sidebar
                title="정비"
                titleIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                    </svg>
                }
                menuItems={repairMenuItems}
                activeItem={activeTab}
                onSelect={handleMenuSelect}
            />
            <div className="repair-content" style={{ padding: "0", display: "flex", flexDirection: "column" }}>
                <Routes>
                    <Route
                        path="diagnosis"
                        element={
                            <DiagnosisView
                                diagnosisResult={diagnosisResult}
                                setDiagnosisResult={setDiagnosisResult}
                                onNext={() => handleMenuSelect("recommendation")}
                            />
                        }
                    />
                    <Route
                        path="recommendation"
                        element={
                            <RecommendationView
                                diagnosisResult={diagnosisResult}
                                selectedShop={selectedShop}
                                setSelectedShop={setSelectedShop}
                                onNext={() => handleMenuSelect("request")}
                            />
                        }
                    />
                    <Route
                        path="request"
                        element={
                            <RequestView
                                diagnosisResult={diagnosisResult}
                                selectedShop={selectedShop}
                                onComplete={() => handleMenuSelect("status")}
                            />
                        }
                    />
                    <Route path="status" element={<StatusView />} />
                    <Route path="*" element={<Navigate to="diagnosis" replace />} />
                </Routes>
            </div>
        </div>
    );
}
